'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Phone, Mail, User, LogOut } from 'lucide-react';
import { SITE_CONFIG } from '@/lib/config';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Apartments', href: '/apartments-listing' },
  { label: 'About', href: '/#about' },
  { label: 'Amenities', href: '/#amenities' },
  { label: 'Contact', href: '/#contact' },
];

export default function PublicHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {}
  };

  return (
    <>
      {/* Top info bar — desktop only */}
      <div className="hidden md:block bg-[#050D1A] text-[#C9A96E] text-xs py-2.5">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a
              href={`tel:${SITE_CONFIG?.phone?.replace(/\s/g, '')}`}
              className="flex items-center gap-1.5 hover:text-white transition-colors tracking-wide"
            >
              <Phone size={11} />
              <span>{SITE_CONFIG?.phone}</span>
            </a>
            <a
              href={`mailto:${SITE_CONFIG?.email}`}
              className="flex items-center gap-1.5 hover:text-white transition-colors tracking-wide"
            >
              <Mail size={11} />
              <span>{SITE_CONFIG?.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-5">
            <a
              href={SITE_CONFIG?.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors font-medium tracking-widest text-xs uppercase"
            >
              Instagram · Java Apartments
            </a>
            <span className="text-[#C9A96E]/30">|</span>
            <a
              href={SITE_CONFIG?.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors font-medium tracking-widest text-xs uppercase"
            >
              TikTok · Java Apartments
            </a>
          </div>
        </div>
      </div>
      {/* Main nav */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-md shadow-[0_1px_0_0_#E2DDD6,0_4px_24px_rgba(11,22,40,0.08)]'
            : 'bg-white border-b border-[#E2DDD6]'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3.5 group shrink-0">
              <AppLogo size={42} variant="dark" />
              <div className="flex flex-col">
                <span
                  className="leading-none text-[#0B1628] group-hover:text-[#162340] transition-colors"
                  style={{
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontSize: '1rem',
                    fontWeight: 400,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                  }}
                >
                  JAVA
                </span>
                <span
                  className="leading-none mt-0.5"
                  style={{
                    fontSize: '0.55rem',
                    fontWeight: 600,
                    letterSpacing: '0.22em',
                    color: '#C9A96E',
                    textTransform: 'uppercase',
                  }}
                >
                  APARTMENTS
                </span>
              </div>
            </Link>

            {/* Desktop nav — centered */}
            <nav className="hidden md:flex items-center gap-0 absolute left-1/2 -translate-x-1/2">
              {navLinks?.map((link) => (
                <Link
                  key={`nav-${link?.label}`}
                  href={link?.href}
                  className="px-5 py-2 text-xs font-semibold text-[#6B6560] hover:text-[#0B1628] transition-colors tracking-widest uppercase relative group"
                  style={{ letterSpacing: '0.12em' }}
                >
                  {link?.label}
                  <span
                    className="absolute bottom-0 left-5 right-5 h-px bg-[#C9A96E] scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"
                  />
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              {user ? (
                <>
                  <Link
                    href="/guest-dashboard"
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-[#6B6560] hover:text-[#0B1628] transition-colors tracking-widest uppercase"
                    style={{ letterSpacing: '0.12em' }}
                  >
                    <User size={13} />
                    My Bookings
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-[#6B6560] hover:text-[#0B1628] transition-colors tracking-widest uppercase"
                    style={{ letterSpacing: '0.12em' }}
                  >
                    <LogOut size={13} />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  href="/login"
                  className="px-4 py-2 text-xs font-semibold text-[#6B6560] hover:text-[#0B1628] transition-colors tracking-widest uppercase"
                  style={{ letterSpacing: '0.12em' }}
                >
                  Sign In
                </Link>
              )}
              <Link
                href="/apartments-listing"
                className="btn-gold text-xs"
                style={{ letterSpacing: '0.1em' }}
              >
                Book Now
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-[#0B1628] hover:text-[#C9A96E] transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className="md:hidden border-t border-[#E2DDD6] bg-white fade-in"
            style={{ boxShadow: '0 8px 32px rgba(11,22,40,0.10)' }}
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navLinks?.map((link) => (
                <Link
                  key={`mobile-nav-${link?.label}`}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-0 py-3.5 text-sm font-semibold text-[#0B1628] hover:text-[#C9A96E] transition-colors tracking-widest uppercase border-b border-[#F0EDE8] last:border-0"
                  style={{ letterSpacing: '0.12em' }}
                >
                  {link?.label}
                </Link>
              ))}
              <div className="pt-5 flex flex-col gap-3">
                {user ? (
                  <>
                    <Link
                      href="/guest-dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="btn-outline text-xs w-full justify-center flex items-center gap-2"
                      style={{ letterSpacing: '0.1em' }}
                    >
                      <User size={13} />
                      My Bookings
                    </Link>
                    <button
                      onClick={() => { setMobileOpen(false); handleSignOut(); }}
                      className="btn-outline text-xs w-full justify-center flex items-center gap-2"
                      style={{ letterSpacing: '0.1em' }}
                    >
                      <LogOut size={13} />
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link
                    href="/login"
                    onClick={() => setMobileOpen(false)}
                    className="btn-outline text-xs w-full justify-center"
                    style={{ letterSpacing: '0.1em' }}
                  >
                    Sign In
                  </Link>
                )}
                <Link
                  href="/apartments-listing"
                  onClick={() => setMobileOpen(false)}
                  className="btn-gold text-xs w-full justify-center"
                  style={{ letterSpacing: '0.1em' }}
                >
                  Book Now
                </Link>
              </div>
              <div className="pt-4 flex items-center gap-4 text-xs text-[#6B6560]">
                <a
                  href={`tel:${SITE_CONFIG?.phone?.replace(/\s/g, '')}`}
                  className="flex items-center gap-1.5 hover:text-[#C9A96E] transition-colors"
                >
                  <Phone size={11} /> {SITE_CONFIG?.phone}
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}